import React, { createContext, useContext, useMemo } from "react";
import { brands, BrandConfig } from "./brands";
import { useMetroConfig } from "../contexts/MetroConfigContext";

const BrandContext = createContext<BrandConfig>(brands.coldwellbanker);

export const BrandProvider: React.FC<{
  brandKey?: string;
  children: React.ReactNode;
}> = ({ brandKey = "coldwellbanker", children }) => {
  let baseBrand = brands[brandKey] || brands.coldwellbanker;
  const { metroConfig, docId } = useMetroConfig();

  // Narrowly extract brandKey from arbitrary metroConfig.data shape
  const extractBrandInfo = (data: unknown): { brandKey?: string; metroId?: string } => {
    if (!data || typeof data !== 'object') return {};
    const rec = data as Record<string, unknown>;
    const brandKey = typeof rec['brandKey'] === 'string' ? rec['brandKey'] : undefined;
    const metroId = typeof rec['metroId'] === 'string' ? rec['metroId'] : undefined;
    return { brandKey, metroId };
  };
  const hasCurrentMetroConfig = !!docId && metroConfig?.docId === docId && metroConfig?.isLoaded === true && metroConfig?.loading === false;
  const { brandKey: inferredBrandKey, metroId: inferredMetroId } = hasCurrentMetroConfig
    ? extractBrandInfo(metroConfig.data)
    : { brandKey: undefined, metroId: undefined };
  // Only override if recognized; otherwise keep provided brandKey/default
  let normalizedKey = brandKey;
  if (inferredMetroId === 'LOC-648') {
    normalizedKey = 'bain';
  } else if (inferredBrandKey) {
    if (/coldwell banker/i.test(inferredBrandKey)) {
      normalizedKey = 'coldwellbanker';
    } else if (/christie/i.test(inferredBrandKey)) {
      normalizedKey = 'warburg';
    } else if (/warburg/i.test(inferredBrandKey)) {
      normalizedKey = 'warburg';
    } else if (/sotheby/i.test(inferredBrandKey)) {
      normalizedKey = 'sothebys';
    } else if (/corcoran/i.test(inferredBrandKey)) {
      normalizedKey = 'corcorangroup';
    } else if (/compass/i.test(inferredBrandKey) || inferredBrandKey.toUpperCase() === 'COM') {
      normalizedKey = 'compass';
    }
  }
  baseBrand = brands[normalizedKey] || brands.coldwellbanker;

  const resolvedBrand = useMemo<BrandConfig>(() => {
    // Hide logo if docId is not provided
    if (!docId) {
      return {
        ...baseBrand,
        logo: null,
      };
    }
    
    return baseBrand;
  }, [baseBrand, docId]);

  return (
    <BrandContext.Provider value={resolvedBrand}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = () => useContext(BrandContext);
